import React, { useState, useEffect } from "react"
import { Check, CaretUpDown } from "@phosphor-icons/react"
import { Typography } from "@/lib/typography"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { getStartups } from "@/lib/api"
import { SHARED_UI_LABELS } from "@/config/task-display-config"

/**
 * StartupPicker Component
 * A searchable combobox to select a Startup by ID (UUID).
 * 
 * Props:
 * - value: string (startup_id)
 * - onChange: (id, name) => void
 * - error: boolean
 * - disabled: boolean
 */
export function StartupPicker({ value, onChange, error, disabled }) {
    const [open, setOpen] = useState(false)
    const [startups, setStartups] = useState([])
    const [loading, setLoading] = useState(false)

    // Search state to potentially debounce if list is huge (Client-side for now)
    const [search, setSearch] = useState("")

    // Load Startups on Mount
    useEffect(() => {
        async function loadStartups() {
            setLoading(true)
            try {
                // Fetch all startups (assuming < 1000 for standard usage)
                // Ideally should be /api/startups?limit=1000
                const data = await getStartups()
                if (Array.isArray(data)) {
                    // Map to { value: id, label: name, status: status }
                    const formatted = data.map(s => ({
                        value: s.id,
                        label: s.name,
                        status: s.status
                    }))
                    setStartups(formatted)
                }
            } catch (err) {
                console.error("Failed to load startups for picker", err)
            } finally {
                setLoading(false)
            }
        }
        loadStartups()
    }, [])

    const selectedStartup = startups.find((s) => s.value === value)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    disabled={disabled}
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between font-normal",
                        !value && "text-muted-foreground",
                        error && "border-red-500 ring-1 ring-red-500"
                    )}
                >
                    {selectedStartup ? (
                        <span className="flex items-center gap-2">
                            {selectedStartup.label}
                            {selectedStartup.status && (
                                <span className={`${Typography.caption} px-2 py-0.5 rounded bg-slate-50 text-slate-500`}>
                                    {selectedStartup.status}
                                </span>
                            )}
                        </span>
                    ) : (
                        loading ? "Loading startups..." : "Select startup..."
                    )}
                    <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0">
                <Command>
                    <CommandInput placeholder={SHARED_UI_LABELS.searchStartup} onValueChange={setSearch} />
                    <CommandList>
                        <CommandEmpty>No startup found.</CommandEmpty>
                        <CommandGroup>
                            {startups.map((startup) => (
                                <CommandItem
                                    key={startup.value}
                                    value={startup.label} // Command searches by this value (Name)
                                    onSelect={() => {
                                        onChange(startup.value, startup.label)
                                        setOpen(false)
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value === startup.value ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <div className="flex flex-col">
                                        <span>{startup.label}</span>
                                        {startup.status && (
                                            <span className="text-[10px] text-slate-400">{startup.status}</span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
